import SEOHead from '@/components/SEOHead';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, MapPin, Euro, Shield, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getLocalBusinessSchema, getFAQSchema, getBreadcrumbSchema } from '@/utils/seoSchemas';

export default function MorgesGestion() {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "Quels quartiers de Morges sont les plus rentables pour la location saisonnière ?",
      answer: "Le centre-ville (Grand-Rue, vieille ville piétonne, château) et les quais (Parc de l'Indépendance, port) sont les plus prisés. Les secteurs des Charpentiers et de Beausobre offrent proximité lac et commerces. La Fête de la Tulipe (fin mars-début mai, 300k visiteurs) génère une forte demande printanière."
    },
    {
      question: "Quelle est la réglementation pour l'Airbnb à Morges ?",
      answer: "À Morges (canton de Vaud), la location saisonnière est limitée à 90 nuits/an (LEAE). Vous devez vous annoncer auprès de la commune, tenir un registre des voyageurs et collecter la taxe de séjour. Vérifiez les restrictions de votre PPE."
    },
    {
      question: "Combien coûte votre service de conciergerie à Morges ?",
      answer: "Notre tarification est de 22% de commission sur les revenus locatifs + 80 CHF/mois de frais de gestion. Ce tarif inclut l'ensemble des services (annonce, pricing, ménages, check-in/out, maintenance)."
    },
    {
      question: "Proposez-vous des photos professionnelles ?",
      answer: "Oui, nous incluons un shooting photo professionnel dans notre prestation pour maximiser l'attractivité de votre annonce."
    }
  ];

  const schemas = [
    getLocalBusinessSchema('Morges', 'Vaud', 46.511, 6.499),
    getFAQSchema(faqs),
    getBreadcrumbSchema([
      { name: 'Accueil', url: 'https://homely-conciergerie.ch' },
      { name: 'Gestion Locative', url: 'https://homely-conciergerie.ch/nos-villes' },
      { name: 'Morges', url: 'https://homely-conciergerie.ch/gestion-locative/morges' }
    ])
  ];

  return (
    <Layout>
      <SEOHead
        title="Gestion Location Saisonnière Morges | Conciergerie Airbnb"
        description="Conciergerie Airbnb et gestion locative saisonnière à Morges. Commission 22% + 80 CHF/mois. Service complet : annonce, pricing dynamique, ménages, check-in, maintenance."
        canonical="https://homely-conciergerie.ch/gestion-locative/morges"
        schemas={schemas}
      />

      <div className="min-h-screen bg-[#F5F3EF]">
        {/* Hero */}
        <section 
          className="relative py-32 px-6 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(43, 43, 43, 0.7), rgba(43, 43, 43, 0.85)), url('https://images.unsplash.com/photo-1765496888350-2a41bb618049?w=1920&q=85')`,
          }}
        >
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Gestion Locative Saisonnière<br />
              <span className="text-[#8A9474]">Morges</span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-4 leading-relaxed">
              Conciergerie Airbnb complète pour appartements, maisons et villas
            </p>
            
            <p className="text-lg mb-8 text-gray-200">
              Lac Léman | Fête de la Tulipe | Qualité de vie
            </p>

            <Button 
              onClick={() => navigate('/estimation')}
              size="lg"
              className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-4 md:px-10 py-6 md:py-7 text-sm md:text-lg font-semibold whitespace-normal md:whitespace-nowrap"
              data-testid="hero-cta-estimation"
            >
              Estimer Votre Bien à Morges
            </Button>
          </div>
        </section>

        {/* Marché Local */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6">
              <MapPin className="inline h-8 w-8 text-[#8A9474] mr-2" />
              Le Marché de la Location Saisonnière à Morges
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 items-center mb-6">
              <div className="prose prose-lg max-w-none text-[#4A4A4A] space-y-4">
                <p>
                  Surnommée « Fleur du Léman », Morges est une destination slow tourisme prisée à 8-10 km de Lausanne. L'attractivité locative saisonnière repose sur son cadre lémanique (château, port, quais fleuris avec vue Mont-Blanc), sa vieille ville piétonne et surtout la Fête de la Tulipe, événement gratuit qui attire 300 000 visiteurs chaque printemps (27 mars-10 mai 2026 : 140 000 fleurs, 350 variétés).
                </p>
                <p>
                  Le centre-ville (Grand-Rue, commerces, restaurants) et les quais (Parc de l'Indépendance) concentrent l'animation et l'accessibilité. Les quartiers résidentiels des Charpentiers et de Beausobre séduisent pour leur proximité lac et commodités. La Fête de la Tulipe génère une demande forte en avril-mai, complétée par la Fête du Dahlia (juillet-octobre). La région est également une porte d'accès aux vignobles de La Côte et bénéficie d'une excellente desserte (train, bateau CGN, route).
                </p>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1765496888350-2a41bb618049?w=800&q=85" 
                  alt="Morges Fête Tulipe - Airbnb bord lac famille" 
                  className="w-full h-[300px] object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6">
              <Shield className="inline h-8 w-8 text-[#8A9474] mr-2" />
              Nos Services de Conciergerie à Morges
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: "Création d'annonce optimisée", desc: "Photos professionnelles, description SEO, mise en valeur des atouts de votre bien" },
                { title: "Pricing dynamique", desc: "Ajustement quotidien des tarifs selon la demande locale et les événements" },
                { title: "Gestion des réservations", desc: "Communication avec les voyageurs, validation des profils, gestion du calendrier" },
                { title: "Ménages professionnels", desc: "Prestataires qualifiés, contrôle qualité, rotation rapide entre les séjours" },
                { title: "Accueil des voyageurs", desc: "Check-in/check-out flexible, remise des clés, explications du logement" },
                { title: "Maintenance & interventions", desc: "Réseau d'artisans locaux, gestion des incidents, entretien préventif" },
                { title: "Reporting mensuel", desc: "Suivi des revenus, statistiques d'occupation, recommandations d'optimisation" },
                { title: "Service client 7j/7", desc: "Équipe locale disponible pour vous et vos voyageurs" }
              ].map((service, idx) => (
                <Card key={`idx-${idx}`} className="border-2 border-[#E5E2DB]">
                  <CardContent className="p-6">
                    <CheckCircle2 className="h-6 w-6 text-[#8A9474] mb-3" />
                    <h4 className="font-bold text-[#2B2B2B] mb-2">{service.title}</h4>
                    <p className="text-sm text-[#4A4A4A]">{service.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Tarification */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6">
              <Euro className="inline h-8 w-8 text-[#8A9474] mr-2" />
              Tarification Transparente
            </h2>
            
            <Card className="border-2 border-[#8A9474]">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-[#8A9474] mb-2">22% + 80 CHF/mois</div>
                  <div className="text-sm text-[#4A4A4A]">Commission sur revenus locatifs + frais de gestion</div>
                </div>
                <ul className="space-y-3 text-[#4A4A4A]">
                  {[
                    'Aucun frais caché',
                    'Tous les services inclus',
                    'Paiement mensuel garanti',
                    'Sans engagement de durée minimum'
                  ].map((item, i) => (
                    <li key={`i-${i}`} className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-[#8A9474] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Réglementation */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6">
              <Calendar className="inline h-8 w-8 text-[#8A9474] mr-2" />
              Réglementation Locale
            </h2>
            
            <div className="bg-[#E8EBE4] p-6 rounded-xl">
              <p className="text-[#4A4A4A] mb-4">
                À Morges (canton de Vaud), la location saisonnière est limitée à 90 nuits par an en vertu de la loi vaudoise (LEAE). Vous devez vous annoncer auprès de la commune de Morges, tenir un registre des voyageurs et collecter la taxe de séjour obligatoire.
              </p>
              <p className="text-[#4A4A4A]">
                Certains règlements de copropriété (PPE) peuvent imposer des restrictions spécifiques. Nous vous recommandons de vérifier votre statut auprès du service du logement de la commune de Morges et de consulter le règlement de votre PPE.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8 text-center">
              Questions Fréquentes
            </h2>
            
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={`idx-${idx}`} className="border-2 border-[#E5E2DB]">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-[#2B2B2B] mb-3">{faq.question}</h4>
                    <p className="text-[#4A4A4A]">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 px-6 bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Prêt à Confier Votre Bien à Morges ?
            </h2>
            <p className="text-xl mb-8 text-gray-200">
              Recevez une estimation gratuite de vos revenus locatifs potentiels
            </p>
            <Button 
              onClick={() => navigate('/estimation')}
              size="lg"
              className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-4 md:px-10 py-6 md:py-7 text-sm md:text-lg font-semibold whitespace-normal md:whitespace-nowrap"
              data-testid="cta-final-estimation"
            >
              Obtenir Mon Estimation Gratuite
            </Button>
          </div>
        </section>
      </div>
    </Layout>
  );
}
